import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useActiveTenantId } from '@/store/tenantStore'
import {
  Alert,
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
  useToast,
} from '@biartbt/biartd'
import { organizationsApi, type Organization } from '@/modules/system/organizations/api/organizationsApi'
import { departmentsApi, type Department } from '@/modules/organization/departments/api/departmentsApi'
import { employeesApi, type Employee, type EmployeeLookupsResponse } from '../api/employeesApi'
import { lookupsApi, type CityLookup, type CountryLookup, type TownLookup } from '@/modules/system/tenants/api/lookupsApi'
import { tenantsApi, type Tenant } from '@/modules/system/tenants/api/tenantsApi'

type TabId = 'personal' | 'job' | 'employment' | 'timeoff' | 'documents' | 'performance' | 'timesheet'

type Form = {
  employeeNumber: string
  name: string
  secondName: string
  surname: string
  email: string
  phone: string
  birthDate: string
  sex: 'M' | 'F'
  hireDate: string
  outDate: string
  profileImage: string
  firmId: string
  organizationId: string
  locationId: string
  departmentId: string
  unitId: string
  subUnitId: string
  positionId: string
  jobId: string
  titleId: string
  salary: string
  salaryCurrency: string
  paymentFrequency: string
  calculationType: string
  paymentMethod: string
  salaryWorkType: string
  minimumWage: boolean
  employeeType: string
  licenseClass: string
  licenseDate: string
  education: string
  militaryStatus: string
  idTcNo: string
  birthPlace: string
  serialNo: string
  validityDate: string
  nationality: string
  motherName: string
  fatherName: string
  neighborhood: string
  street: string
  buildingNo: string
  apartmentNo: string
  district: string
  province: string
  country: string
  postalCode: string
  emergencyContactName: string
  emergencyContactPhone: string
  emergencyContactRelation: string
  maritalStatus: string
  sscNo: string
  sscStatus: string
  insuranceStartDate: string
  sscRegisteredName: string
  sscRegisteredSurname: string
  bankBranch: string
  branchName: string
  bankCode: string
  bankName: string
  incomeTaxExemption: boolean
  stampTaxExemption: boolean
  minimumWageIncomeTaxExemption: boolean
  minimumWageStampTaxExemption: boolean
  discountLaw: string
  laborLaw: string
  employeeDocumentType: string
  rndLaw: string
  employmentLaw: string
  employmentIncentiveEndDate: string
  disabilityDegree: string
  disabilityRate: string
  disabilityGroup: string
  iban: string
  accountNo: string
  contractType: string
  contractBegDate: string
  contractEndDate: string
  workCalendar: string
  dutyType: string
  workMode: string
  monthlyWorkDays: string
  weeklyWorkHours: string
  trialPeriodMonths: string
  trialPeriodEndSalary: string
  isActive: boolean
}

// Pearl employee form birebir: sekme ve bölüm isimleri (sıra ve etiketler)
const tabs: Array<{ id: TabId; label: string }> = [
  { id: 'personal', label: 'Kişisel Bilgiler' },
  { id: 'job', label: 'Organizasyon' },
  { id: 'employment', label: 'Çalışma Bilgileri' },
  { id: 'timeoff', label: 'İzin' },
  { id: 'documents', label: 'Belgeler' },
  { id: 'performance', label: 'Performans' },
  { id: 'timesheet', label: 'Puantaj' },
]

const emptyForm: Form = {
  employeeNumber: '',
  name: '',
  secondName: '',
  surname: '',
  email: '',
  phone: '',
  birthDate: '',
  sex: 'M',
  hireDate: '',
  outDate: '',
  profileImage: '',
  firmId: '',
  organizationId: '',
  locationId: '',
  departmentId: '',
  unitId: '',
  subUnitId: '',
  positionId: '',
  jobId: '',
  titleId: '',
  salary: '',
  salaryCurrency: 'TRY',
  paymentFrequency: '',
  calculationType: '',
  paymentMethod: '',
  salaryWorkType: '',
  minimumWage: false,
  employeeType: '',
  licenseClass: '',
  licenseDate: '',
  education: '',
  militaryStatus: '',
  idTcNo: '',
  birthPlace: '',
  serialNo: '',
  validityDate: '',
  nationality: '',
  motherName: '',
  fatherName: '',
  neighborhood: '',
  street: '',
  buildingNo: '',
  apartmentNo: '',
  district: '',
  province: '',
  country: '',
  postalCode: '',
  emergencyContactName: '',
  emergencyContactPhone: '',
  emergencyContactRelation: '',
  maritalStatus: '',
  sscNo: '',
  sscStatus: '',
  insuranceStartDate: '',
  sscRegisteredName: '',
  sscRegisteredSurname: '',
  bankBranch: '',
  branchName: '',
  bankCode: '',
  bankName: '',
  incomeTaxExemption: false,
  stampTaxExemption: false,
  minimumWageIncomeTaxExemption: false,
  minimumWageStampTaxExemption: false,
  discountLaw: '',
  laborLaw: '',
  employeeDocumentType: '',
  rndLaw: '',
  employmentLaw: '',
  employmentIncentiveEndDate: '',
  disabilityDegree: '',
  disabilityRate: '',
  disabilityGroup: '',
  iban: '',
  accountNo: '',
  contractType: '',
  contractBegDate: '',
  contractEndDate: '',
  workCalendar: '',
  dutyType: '',
  workMode: '',
  monthlyWorkDays: '',
  weeklyWorkHours: '',
  trialPeriodMonths: '',
  trialPeriodEndSalary: '',
  isActive: true,
}

const toIsoDate = (v: string): string | null => (v ? new Date(`${v}T00:00:00`).toISOString() : null)
const toDateInput = (v?: string | null): string => (v ? v.slice(0, 10) : '')
const emptyToNull = (v: string): string | null => (v.trim() ? v.trim() : null)
const emptyToNumber = (v: string): number | null => {
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

const fallbackEmployeeLookups: EmployeeLookupsResponse = {
  genders: [],
  employeeTypes: [],
  positions: [],
  jobs: [],
  titles: [],
  militaryStatuses: [],
  maritalStatuses: [],
  emergencyRelations: [],
  educationLevels: [],
  graduationStatuses: [],
  educationTypes: [],
  driverLicenses: [],
  paymentFrequencies: [],
  calculationTypes: [],
  paymentMethods: [],
  salaryWorkTypes: [],
  workCalendars: [],
  dutyTypes: [],
  workModes: [],
  contractTypes: [],
  discountLaws: [],
  laborLaws: [],
  employeeDocumentTypes: [],
  rndLaws: [],
  employmentLaws: [],
  disabilityDegrees: [],
  bankBranches: [],
  timeOffTypes: [],
  approvalStatuses: [],
}

export default function EmployeeNewPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { employeeId: employeeIdParam } = useParams()
  const [searchParams] = useSearchParams()
  const activeTenantId = useActiveTenantId()
  const tenantIdFromUrl = searchParams.get('tenantId')
  const tenantId = tenantIdFromUrl ? Number(tenantIdFromUrl) : activeTenantId
  const routeEmployeeId = employeeIdParam ? Number(employeeIdParam) : null
  const employeeFromNavigation = (location.state as { employeeFromList?: Employee } | null)?.employeeFromList
  const { toast } = useToast()

  const [activeTab, setActiveTab] = useState<TabId>('personal')
  const [form, setForm] = useState<Form>(emptyForm)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [employeeId, setEmployeeId] = useState<number | null>(null)
  const [lookupsLoading, setLookupsLoading] = useState(false)

  const [tenants, setTenants] = useState<Pick<Tenant, 'tenantId' | 'name'>[]>([])
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [departments, setDepartments] = useState<Department[]>([])
  const [countries, setCountries] = useState<CountryLookup[]>([])
  const [cities, setCities] = useState<CityLookup[]>([])
  const [towns, setTowns] = useState<TownLookup[]>([])
  const [employeeLookups, setEmployeeLookups] = useState<EmployeeLookupsResponse>(fallbackEmployeeLookups)

  const [educations, setEducations] = useState<any[]>([])
  const [showEducationModal, setShowEducationModal] = useState(false)
  const [educationForm, setEducationForm] = useState({
    educationLevel: '',
    schoolName: '',
    sectionName: '',
    graduationStatus: '',
    graduationYear: '',
    startYear: '',
    educationType: '',
    diplomaGrade: '',
    countryCity: '',
    diplomaNumber: '',
  })
  const [editingEducationId, setEditingEducationId] = useState<number | null>(null)

  const [timeOffs, setTimeOffs] = useState<any[]>([])
  const [timeOffForm, setTimeOffForm] = useState({ timeOffType: '', startDate: '', endDate: '', approvalStatus: '', notes: '' })
  const [editingTimeOffId, setEditingTimeOffId] = useState<number | null>(null)
  const [timeOffFilter, setTimeOffFilter] = useState<'all' | 'approved' | 'pending' | 'rejected'>('all')
  const [timeOffQuery, setTimeOffQuery] = useState('')

  const [documents, setDocuments] = useState<any[]>([])
  const [documentForm, setDocumentForm] = useState({ name: '', documentType: '', notes: '' })
  const [editingDocumentId, setEditingDocumentId] = useState<number | null>(null)

  const [performances, setPerformances] = useState<any[]>([])
  const [performanceForm, setPerformanceForm] = useState({ reviewDate: '', score: '', reviewer: '' })
  const [editingPerformanceId, setEditingPerformanceId] = useState<number | null>(null)

  const [timesheets, setTimesheets] = useState<any[]>([])
  const [timesheetForm, setTimesheetForm] = useState({ workDate: '', workedHours: '', overtimeHours: '' })
  const [editingTimesheetId, setEditingTimesheetId] = useState<number | null>(null)

  const setField = <K extends keyof Form>(key: K, value: Form[K]) => setForm((s) => ({ ...s, [key]: value }))
  const hydrateFormFromEmployee = useCallback((employee: any) => {
    setForm((current) => ({
      ...current,
      employeeNumber: employee.employeeNumber ?? '',
      name: employee.name ?? '',
      secondName: employee.secondName ?? '',
      surname: employee.surname ?? '',
      email: employee.email ?? '',
      phone: employee.phone ?? '',
      birthDate: toDateInput(employee.birthDate),
      sex: employee.sex === 'F' ? 'F' : 'M',
      hireDate: toDateInput(employee.hireDate),
      outDate: toDateInput(employee.outDate),
      profileImage: employee.profileImage ?? '',
      firmId: employee.tenantId != null ? String(employee.tenantId) : (tenantId ? String(tenantId) : ''),
      organizationId: employee.organizationId != null ? String(employee.organizationId) : '',
      locationId: employee.locationId != null ? String(employee.locationId) : '',
      departmentId: employee.departmentId != null ? String(employee.departmentId) : '',
      unitId: employee.unitId != null ? String(employee.unitId) : '',
      subUnitId: employee.subUnitId != null ? String(employee.subUnitId) : '',
      positionId: employee.positionId != null ? String(employee.positionId) : '',
      jobId: employee.jobId != null ? String(employee.jobId) : '',
      titleId: employee.titleId != null ? String(employee.titleId) : '',
      salary: employee.salary != null ? String(employee.salary) : '',
      salaryCurrency: employee.salaryCurrency ?? 'TRY',
      paymentFrequency: employee.paymentFrequency ?? '',
      calculationType: employee.calculationType ?? '',
      paymentMethod: employee.paymentMethod ?? '',
      salaryWorkType: employee.salaryWorkType ?? '',
      minimumWage: !!employee.minimumWage,
      employeeType: employee.employeeType ?? '',
      licenseClass: employee.licenseClass ?? '',
      licenseDate: toDateInput(employee.licenseDate),
      education: employee.education ?? '',
      militaryStatus: employee.militaryStatus ?? '',
      idTcNo: employee.idTcNo ?? '',
      birthPlace: employee.birthPlace ?? '',
      serialNo: employee.serialNo ?? '',
      validityDate: toDateInput(employee.validityDate),
      nationality: employee.nationality ?? '',
      motherName: employee.motherName ?? '',
      fatherName: employee.fatherName ?? '',
      neighborhood: employee.neighborhood ?? '',
      street: employee.street ?? '',
      buildingNo: employee.buildingNo ?? '',
      apartmentNo: employee.apartmentNo ?? '',
      district: employee.district ?? '',
      province: employee.province ?? '',
      country: employee.country ?? '',
      postalCode: employee.postalCode ?? '',
      emergencyContactName: employee.emergencyContactName ?? '',
      emergencyContactPhone: employee.emergencyContactPhone ?? '',
      emergencyContactRelation: employee.emergencyContactRelation ?? '',
      maritalStatus: employee.maritalStatus ?? '',
      sscNo: employee.sscNo ?? '',
      sscStatus: employee.sscStatus != null ? String(employee.sscStatus) : '',
      insuranceStartDate: toDateInput(employee.insuranceStartDate),
      sscRegisteredName: employee.sscRegisteredName ?? '',
      sscRegisteredSurname: employee.sscRegisteredSurname ?? '',
      bankBranch: employee.bankBranch ?? '',
      branchName: employee.branchName ?? '',
      bankCode: employee.bankCode ?? '',
      bankName: employee.bankName ?? '',
      incomeTaxExemption: !!employee.incomeTaxExemption,
      stampTaxExemption: !!employee.stampTaxExemption,
      minimumWageIncomeTaxExemption: !!employee.minimumWageIncomeTaxExemption,
      minimumWageStampTaxExemption: !!employee.minimumWageStampTaxExemption,
      discountLaw: employee.discountLaw ?? '',
      laborLaw: employee.laborLaw ?? '',
      employeeDocumentType: employee.employeeDocumentType ?? '',
      rndLaw: employee.rndLaw ?? '',
      employmentLaw: employee.employmentLaw ?? '',
      employmentIncentiveEndDate: toDateInput(employee.employmentIncentiveEndDate),
      disabilityDegree: employee.disabilityDegree ?? '',
      disabilityRate: employee.disabilityRate != null ? String(employee.disabilityRate) : '',
      disabilityGroup: employee.disabilityGroup ?? '',
      iban: employee.iban ?? '',
      accountNo: employee.accountNo ?? '',
      contractType: employee.contractType ?? '',
      contractBegDate: toDateInput(employee.contractBegDate),
      contractEndDate: toDateInput(employee.contractEndDate),
      workCalendar: employee.workCalendar ?? '',
      dutyType: employee.dutyType ?? '',
      workMode: employee.workMode ?? '',
      monthlyWorkDays: employee.monthlyWorkDays != null ? String(employee.monthlyWorkDays) : '',
      weeklyWorkHours: employee.weeklyWorkHours != null ? String(employee.weeklyWorkHours) : '',
      trialPeriodMonths: employee.trialPeriodMonths != null ? String(employee.trialPeriodMonths) : '',
      trialPeriodEndSalary: employee.trialPeriodEndSalary != null ? String(employee.trialPeriodEndSalary) : '',
      isActive: employee.isActive ?? true,
    }))
  }, [])

  const calculateDurationDays = useCallback((startDate?: string, endDate?: string): number | null => {
    if (!startDate || !endDate) return null
    const start = new Date(startDate)
    const end = new Date(endDate)
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return null
    const oneDay = 24 * 60 * 60 * 1000
    return Math.floor((end.getTime() - start.getTime()) / oneDay) + 1
  }, [])

  const normalizeApprovalStatus = useCallback((value?: string | null): 'approved' | 'pending' | 'rejected' => {
    const v = (value ?? '').trim().toLowerCase()
    if (['onaylandi', 'onaylandı', 'approved'].includes(v)) return 'approved'
    if (['reddedildi', 'rejected'].includes(v)) return 'rejected'
    return 'pending'
  }, [])

  const filteredTimeOffs = useMemo(() => {
    return timeOffs.filter((x) => {
      const status = normalizeApprovalStatus(x.approvalStatus)
      const byStatus = timeOffFilter === 'all' ? true : status === timeOffFilter
      const search = timeOffQuery.trim().toLowerCase()
      const byQuery = !search
        ? true
        : `${x.timeOffType ?? ''} ${x.notes ?? ''}`.toLowerCase().includes(search)
      return byStatus && byQuery
    })
  }, [normalizeApprovalStatus, timeOffFilter, timeOffQuery, timeOffs])

  const timeOffStats = useMemo(() => {
    const total = timeOffs.length
    const approved = timeOffs.filter((x) => normalizeApprovalStatus(x.approvalStatus) === 'approved').length
    const pending = timeOffs.filter((x) => normalizeApprovalStatus(x.approvalStatus) === 'pending').length
    const totalDays = timeOffs.reduce((sum, x) => {
      const duration = x.durationDays ?? calculateDurationDays(x.startDate?.slice(0, 10), x.endDate?.slice(0, 10)) ?? 0
      return sum + Number(duration)
    }, 0)
    return { total, approved, pending, totalDays }
  }, [calculateDurationDays, normalizeApprovalStatus, timeOffs])

  const unitOptions = useMemo(() => {
    const departmentId = form.departmentId ? Number(form.departmentId) : null
    if (!departmentId) return []
    return departments.filter((d) => d.parentId === departmentId)
  }, [departments, form.departmentId])

  const subUnitOptions = useMemo(() => {
    const unitId = form.unitId ? Number(form.unitId) : null
    if (!unitId) return []
    return departments.filter((d) => d.parentId === unitId)
  }, [departments, form.unitId])

  const selectedCountry = useMemo(
    () => countries.find((c) => c.name === form.country) ?? null,
    [countries, form.country],
  )

  const selectedCity = useMemo(
    () => cities.find((c) => c.name === form.province) ?? null,
    [cities, form.province],
  )

  const loadOrganizationsByFirm = useCallback(async (firmTenantId: number) => {
    const res = await organizationsApi.list({ tenantId: firmTenantId, pageSize: 500, isActive: true })
    setOrganizations(res.items)
  }, [])

  const loadLookups = useCallback(async () => {
    if (!tenantId) return
    setLookupsLoading(true)
    try {
      const [tenantsRes, orgRes, deptRes, countriesRes] = await Promise.all([
        tenantsApi.list({ pageSize: 200 }),
        organizationsApi.list({ tenantId, pageSize: 500, isActive: true }),
        departmentsApi.list({ tenantId, pageSize: 500, includeDeleted: false }),
        lookupsApi.getCountries({ lang: 'tr' }),
      ])
      setTenants(tenantsRes.items.map((t) => ({ tenantId: t.tenantId, name: t.name })))
      setOrganizations(orgRes.items)
      setDepartments(deptRes.items)
      setCountries(Array.isArray(countriesRes) ? countriesRes : [])
      try {
        const lookups = await employeesApi.getLookups()
        setEmployeeLookups({
          genders: lookups.genders?.length ? lookups.genders : fallbackEmployeeLookups.genders,
          employeeTypes: lookups.employeeTypes?.length ? lookups.employeeTypes : fallbackEmployeeLookups.employeeTypes,
          positions: lookups.positions?.length ? lookups.positions : fallbackEmployeeLookups.positions,
          jobs: lookups.jobs?.length ? lookups.jobs : fallbackEmployeeLookups.jobs,
          titles: lookups.titles?.length ? lookups.titles : fallbackEmployeeLookups.titles,
          militaryStatuses: lookups.militaryStatuses?.length ? lookups.militaryStatuses : fallbackEmployeeLookups.militaryStatuses,
          driverLicenses: lookups.driverLicenses?.length ? lookups.driverLicenses : fallbackEmployeeLookups.driverLicenses,
          maritalStatuses: lookups.maritalStatuses?.length ? lookups.maritalStatuses : fallbackEmployeeLookups.maritalStatuses,
          emergencyRelations: lookups.emergencyRelations?.length ? lookups.emergencyRelations : fallbackEmployeeLookups.emergencyRelations,
          educationLevels: lookups.educationLevels?.length ? lookups.educationLevels : fallbackEmployeeLookups.educationLevels,
          graduationStatuses: lookups.graduationStatuses?.length ? lookups.graduationStatuses : fallbackEmployeeLookups.graduationStatuses,
          educationTypes: lookups.educationTypes?.length ? lookups.educationTypes : fallbackEmployeeLookups.educationTypes,
          paymentFrequencies: lookups.paymentFrequencies?.length ? lookups.paymentFrequencies : fallbackEmployeeLookups.paymentFrequencies,
          calculationTypes: lookups.calculationTypes?.length ? lookups.calculationTypes : fallbackEmployeeLookups.calculationTypes,
          paymentMethods: lookups.paymentMethods?.length ? lookups.paymentMethods : fallbackEmployeeLookups.paymentMethods,
          salaryWorkTypes: lookups.salaryWorkTypes?.length ? lookups.salaryWorkTypes : fallbackEmployeeLookups.salaryWorkTypes,
          workCalendars: lookups.workCalendars?.length ? lookups.workCalendars : fallbackEmployeeLookups.workCalendars,
          dutyTypes: lookups.dutyTypes?.length ? lookups.dutyTypes : fallbackEmployeeLookups.dutyTypes,
          workModes: lookups.workModes?.length ? lookups.workModes : fallbackEmployeeLookups.workModes,
          contractTypes: lookups.contractTypes?.length ? lookups.contractTypes : fallbackEmployeeLookups.contractTypes,
          discountLaws: lookups.discountLaws?.length ? lookups.discountLaws : fallbackEmployeeLookups.discountLaws,
          laborLaws: lookups.laborLaws?.length ? lookups.laborLaws : fallbackEmployeeLookups.laborLaws,
          employeeDocumentTypes: lookups.employeeDocumentTypes?.length ? lookups.employeeDocumentTypes : fallbackEmployeeLookups.employeeDocumentTypes,
          rndLaws: lookups.rndLaws?.length ? lookups.rndLaws : fallbackEmployeeLookups.rndLaws,
          employmentLaws: lookups.employmentLaws?.length ? lookups.employmentLaws : fallbackEmployeeLookups.employmentLaws,
          disabilityDegrees: lookups.disabilityDegrees?.length ? lookups.disabilityDegrees : fallbackEmployeeLookups.disabilityDegrees,
          bankBranches: lookups.bankBranches?.length ? lookups.bankBranches : fallbackEmployeeLookups.bankBranches,
          timeOffTypes: lookups.timeOffTypes?.length ? lookups.timeOffTypes : fallbackEmployeeLookups.timeOffTypes,
          approvalStatuses: lookups.approvalStatuses?.length ? lookups.approvalStatuses : fallbackEmployeeLookups.approvalStatuses,
        })
      } catch {
        setEmployeeLookups(fallbackEmployeeLookups)
        setCountries([])
      }
    } finally {
      setLookupsLoading(false)
    }
  }, [tenantId, loadOrganizationsByFirm])

  const loadTabData = useCallback(async () => {
    if (!employeeId) return
    if (activeTab === 'personal') {
      const res = await employeesApi.educations.list(employeeId, tenantId ?? undefined)
      setEducations(res.items ?? [])
    }
    if (activeTab === 'timeoff') {
      const res = await employeesApi.timeOffs.list(employeeId, tenantId ?? undefined)
      setTimeOffs(res.items ?? [])
    }
    if (activeTab === 'documents') {
      const res = await employeesApi.documents.list(employeeId, tenantId ?? undefined)
      setDocuments(res.items ?? [])
    }
    if (activeTab === 'performance') {
      const res = await employeesApi.performances.list(employeeId, tenantId ?? undefined)
      setPerformances(res.items ?? [])
    }
    if (activeTab === 'timesheet') {
      const res = await employeesApi.timesheets.list(employeeId, tenantId ?? undefined)
      setTimesheets(res.items ?? [])
    }
  }, [activeTab, employeeId, tenantId])

  useEffect(() => {
    loadLookups()
  }, [loadLookups])

  // Initialize firmId for new employees
  useEffect(() => {
    if (tenantId && !form.firmId) {
      setForm((s) => ({ ...s, firmId: String(tenantId) }))
    }
  }, [tenantId, form.firmId])

  useEffect(() => {
    if (!selectedCountry) {
      setCities([])
      setTowns([])
      return
    }

    let active = true
    void (async () => {
      try {
        const cityList = await lookupsApi.getCities(selectedCountry.id, { lang: 'tr' })
        if (!active) return
        setCities(Array.isArray(cityList) ? cityList : [])
      } catch {
        if (!active) return
        setCities([])
      }
    })()

    return () => {
      active = false
    }
  }, [selectedCountry])

  useEffect(() => {
    if (!selectedCity) {
      setTowns([])
      return
    }

    let active = true
    void (async () => {
      try {
        const townList = await lookupsApi.getTowns(selectedCity.id, { lang: 'tr' })
        if (!active) return
        setTowns(Array.isArray(townList) ? townList : [])
      } catch {
        if (!active) return
        setTowns([])
      }
    })()

    return () => {
      active = false
    }
  }, [selectedCity])

  useEffect(() => {
    if (!routeEmployeeId || Number.isNaN(routeEmployeeId)) return

    setEmployeeId(routeEmployeeId)
    if (employeeFromNavigation?.employeeId === routeEmployeeId) {
      hydrateFormFromEmployee(employeeFromNavigation)
    }

    let cancelled = false
    ;(async () => {
      try {
        const employee = await employeesApi.getById(routeEmployeeId, tenantId ?? undefined)
        if (!cancelled) hydrateFormFromEmployee(employee)
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? 'Calisan bilgileri yuklenemedi.')
      }
    })()

    return () => {
      cancelled = true
    }
  }, [employeeFromNavigation, hydrateFormFromEmployee, routeEmployeeId, tenantId])

  useEffect(() => {
    loadTabData()
  }, [loadTabData])

  const onSave = async () => {
    setError(null)
    if (!tenantId) {
      setError('Tenant secimi gerekli.')
      return
    }
    if (!form.employeeNumber.trim() || !form.name.trim() || !form.surname.trim()) {
      setError('Sicil no, ad ve soyad zorunlu.')
      return
    }

    setSaving(true)
    try {
      const payload = {
        tenantId: form.firmId ? Number(form.firmId) : tenantId,
        employeeNumber: form.employeeNumber.trim(),
        name: form.name.trim(),
        surname: form.surname.trim(),
        secondName: emptyToNull(form.secondName),
        email: emptyToNull(form.email),
        phone: emptyToNull(form.phone),
        birthDate: toIsoDate(form.birthDate),
        sex: emptyToNull(form.sex),
        hireDate: toIsoDate(form.hireDate),
        outDate: toIsoDate(form.outDate),
        profileImage: emptyToNull(form.profileImage),
        organizationId: form.organizationId ? emptyToNumber(form.organizationId) : null,
        locationId: form.locationId ? emptyToNumber(form.locationId) : null,
        departmentId: form.departmentId ? emptyToNumber(form.departmentId) : null,
        unitId: form.unitId ? emptyToNumber(form.unitId) : null,
        subUnitId: form.subUnitId ? emptyToNumber(form.subUnitId) : null,
        positionId: form.positionId ? emptyToNumber(form.positionId) : null,
        jobId: form.jobId ? emptyToNumber(form.jobId) : null,
        titleId: form.titleId ? emptyToNumber(form.titleId) : null,
        salary: form.salary ? Number(form.salary) : null,
        salaryCurrency: emptyToNull(form.salaryCurrency),
        paymentFrequency: emptyToNull(form.paymentFrequency),
        calculationType: emptyToNull(form.calculationType),
        paymentMethod: emptyToNull(form.paymentMethod),
        salaryWorkType: emptyToNull(form.salaryWorkType),
        minimumWage: form.minimumWage,
        employeeType: emptyToNull(form.employeeType),
        licenseClass: emptyToNull(form.licenseClass),
        licenseDate: toIsoDate(form.licenseDate),
        education: emptyToNull(form.education),
        militaryStatus: emptyToNull(form.militaryStatus),
        idTcNo: emptyToNull(form.idTcNo),
        birthPlace: emptyToNull(form.birthPlace),
        serialNo: emptyToNull(form.serialNo),
        validityDate: toIsoDate(form.validityDate),
        nationality: emptyToNull(form.nationality),
        motherName: emptyToNull(form.motherName),
        fatherName: emptyToNull(form.fatherName),
        neighborhood: emptyToNull(form.neighborhood),
        street: emptyToNull(form.street),
        buildingNo: emptyToNull(form.buildingNo),
        apartmentNo: emptyToNull(form.apartmentNo),
        district: emptyToNull(form.district),
        province: emptyToNull(form.province),
        country: emptyToNull(form.country),
        postalCode: emptyToNull(form.postalCode),
        emergencyContactName: emptyToNull(form.emergencyContactName),
        emergencyContactPhone: emptyToNull(form.emergencyContactPhone),
        emergencyContactRelation: emptyToNull(form.emergencyContactRelation),
        maritalStatus: emptyToNull(form.maritalStatus),
        sscNo: emptyToNull(form.sscNo),
        sscStatus: form.sscStatus ? Number(form.sscStatus) : null,
        insuranceStartDate: toIsoDate(form.insuranceStartDate),
        sscRegisteredName: emptyToNull(form.sscRegisteredName),
        sscRegisteredSurname: emptyToNull(form.sscRegisteredSurname),
        bankBranch: emptyToNull(form.bankBranch),
        branchName: emptyToNull(form.branchName),
        bankCode: emptyToNull(form.bankCode),
        bankName: emptyToNull(form.bankName),
        incomeTaxExemption: form.incomeTaxExemption,
        stampTaxExemption: form.stampTaxExemption,
        minimumWageIncomeTaxExemption: form.minimumWageIncomeTaxExemption,
        minimumWageStampTaxExemption: form.minimumWageStampTaxExemption,
        discountLaw: emptyToNull(form.discountLaw),
        laborLaw: emptyToNull(form.laborLaw),
        employeeDocumentType: emptyToNull(form.employeeDocumentType),
        rndLaw: emptyToNull(form.rndLaw),
        employmentLaw: emptyToNull(form.employmentLaw),
        employmentIncentiveEndDate: toIsoDate(form.employmentIncentiveEndDate),
        disabilityDegree: emptyToNull(form.disabilityDegree),
        disabilityRate: form.disabilityRate ? Number(form.disabilityRate) : null,
        disabilityGroup: emptyToNull(form.disabilityGroup),
        iban: emptyToNull(form.iban),
        accountNo: emptyToNull(form.accountNo),
        contractType: emptyToNull(form.contractType),
        contractBegDate: toIsoDate(form.contractBegDate),
        contractEndDate: toIsoDate(form.contractEndDate),
        workCalendar: emptyToNull(form.workCalendar),
        dutyType: emptyToNull(form.dutyType),
        workMode: emptyToNull(form.workMode),
        monthlyWorkDays: form.monthlyWorkDays ? Number(form.monthlyWorkDays) : null,
        weeklyWorkHours: form.weeklyWorkHours ? Number(form.weeklyWorkHours) : null,
        trialPeriodMonths: form.trialPeriodMonths ? Number(form.trialPeriodMonths) : null,
        trialPeriodEndSalary: form.trialPeriodEndSalary ? Number(form.trialPeriodEndSalary) : null,
        isActive: form.isActive,
      }

      if (employeeId) {
        await employeesApi.update({ id: employeeId, ...payload })
        toast({ title: 'Başarılı', description: 'Çalışan güncellendi.', variant: 'success' })
      } else {
        const created = await employeesApi.create(payload)
        const id = Number(created?.data?.id ?? 0)
        if (id > 0) {
          setEmployeeId(id)
          const query = tenantId != null ? `?tenantId=${tenantId}` : ''
          navigate(`/hr/employees/${id}/edit${query}`, { replace: true })
        }
        toast({ title: 'Başarılı', description: 'Çalışan oluşturuldu. Düzenleme moduna geçildi.', variant: 'success' })
      }
    } catch (e: any) {
      setError(e?.message ?? 'Kayıt başarısız.')
    } finally {
      setSaving(false)
    }
  }

  const addEducation = async () => {
    if (!employeeId) return
    const payload = {
      educationLevel: emptyToNull(educationForm.educationLevel),
      schoolName: emptyToNull(educationForm.schoolName),
      sectionName: emptyToNull(educationForm.sectionName),
      graduationStatus: emptyToNull(educationForm.graduationStatus),
      graduationYear: educationForm.graduationYear ? Number(educationForm.graduationYear) : null,
      startYear: educationForm.startYear ? Number(educationForm.startYear) : null,
      educationType: emptyToNull(educationForm.educationType),
      diplomaGrade: emptyToNull(educationForm.diplomaGrade),
      countryCity: emptyToNull(educationForm.countryCity),
      diplomaNumber: emptyToNull(educationForm.diplomaNumber),
    }
    try {
      if (editingEducationId) {
        await employeesApi.educations.update(employeeId, editingEducationId, payload)
      } else {
        await employeesApi.educations.create(employeeId, payload)
      }
      setEducationForm({
        educationLevel: '',
        schoolName: '',
        sectionName: '',
        graduationStatus: '',
        graduationYear: '',
        startYear: '',
        educationType: '',
        diplomaGrade: '',
        countryCity: '',
        diplomaNumber: '',
      })
      setEditingEducationId(null)
      setShowEducationModal(false)
      await loadTabData()
      toast({ title: editingEducationId ? 'Öğrenim bilgisi güncellendi' : 'Öğrenim bilgisi eklendi', type: 'success' })
    } catch (err: any) {
      toast({ title: 'Hata', description: err?.message ?? 'Öğrenim bilgisi kaydedilemedi', type: 'error' })
    }
  }

  const addTimeOff = async () => {
    if (!employeeId || !timeOffForm.startDate || !timeOffForm.endDate) return
    const durationDays = calculateDurationDays(timeOffForm.startDate, timeOffForm.endDate)
    const payload = {
      timeOffType: emptyToNull(timeOffForm.timeOffType),
      startDate: toIsoDate(timeOffForm.startDate) as string,
      endDate: toIsoDate(timeOffForm.endDate) as string,
      durationDays: durationDays ?? null,
      approvalStatus: emptyToNull(timeOffForm.approvalStatus),
      notes: emptyToNull(timeOffForm.notes),
    }
    if (editingTimeOffId) {
      await employeesApi.timeOffs.update(employeeId, editingTimeOffId, payload)
    } else {
      await employeesApi.timeOffs.create(employeeId, payload)
    }
    setTimeOffForm({ timeOffType: '', startDate: '', endDate: '', approvalStatus: '', notes: '' })
    setEditingTimeOffId(null)
    await loadTabData()
  }

  const addDocument = async () => {
    if (!employeeId || !documentForm.name.trim()) return
    const payload = {
      name: documentForm.name.trim(),
      documentType: emptyToNull(documentForm.documentType),
      notes: emptyToNull(documentForm.notes),
    }
    if (editingDocumentId) {
      await employeesApi.documents.update(employeeId, editingDocumentId, payload)
    } else {
      await employeesApi.documents.create(employeeId, payload)
    }
    setDocumentForm({ name: '', documentType: '', notes: '' })
    setEditingDocumentId(null)
    await loadTabData()
  }

  const addPerformance = async () => {
    if (!employeeId || !performanceForm.reviewDate) return
    const payload = {
      reviewDate: toIsoDate(performanceForm.reviewDate) as string,
      score: performanceForm.score ? Number(performanceForm.score) : null,
      reviewer: emptyToNull(performanceForm.reviewer),
    }
    if (editingPerformanceId) {
      await employeesApi.performances.update(employeeId, editingPerformanceId, payload)
    } else {
      await employeesApi.performances.create(employeeId, payload)
    }
    setPerformanceForm({ reviewDate: '', score: '', reviewer: '' })
    setEditingPerformanceId(null)
    await loadTabData()
  }

  const addTimesheet = async () => {
    if (!employeeId || !timesheetForm.workDate || !timesheetForm.workedHours) return
    const payload = {
      workDate: toIsoDate(timesheetForm.workDate) as string,
      workedHours: Number(timesheetForm.workedHours),
      overtimeHours: timesheetForm.overtimeHours ? Number(timesheetForm.overtimeHours) : null,
    }
    if (editingTimesheetId) {
      await employeesApi.timesheets.update(employeeId, editingTimesheetId, payload)
    } else {
      await employeesApi.timesheets.create(employeeId, payload)
    }
    setTimesheetForm({ workDate: '', workedHours: '', overtimeHours: '' })
    setEditingTimesheetId(null)
    await loadTabData()
  }

  const deleteEducation = async (id: number) => {
    if (!employeeId) return
    await employeesApi.educations.delete(employeeId, id)
    if (editingEducationId === id) {
      setEditingEducationId(null)
      setEducationForm({
        educationLevel: '',
        schoolName: '',
        sectionName: '',
        graduationStatus: '',
        graduationYear: '',
        startYear: '',
        educationType: '',
        diplomaGrade: '',
        countryCity: '',
        diplomaNumber: '',
      })
    }
    await loadTabData()
  }

  const deleteTimeOff = async (id: number) => {
    if (!employeeId) return
    await employeesApi.timeOffs.delete(employeeId, id)
    if (editingTimeOffId === id) {
      setEditingTimeOffId(null)
      setTimeOffForm({ timeOffType: '', startDate: '', endDate: '', approvalStatus: '', notes: '' })
    }
    await loadTabData()
  }

  const deleteDocument = async (id: number) => {
    if (!employeeId) return
    await employeesApi.documents.delete(employeeId, id)
    if (editingDocumentId === id) {
      setEditingDocumentId(null)
      setDocumentForm({ name: '', documentType: '', notes: '' })
    }
    await loadTabData()
  }

  const deletePerformance = async (id: number) => {
    if (!employeeId) return
    await employeesApi.performances.delete(employeeId, id)
    if (editingPerformanceId === id) {
      setEditingPerformanceId(null)
      setPerformanceForm({ reviewDate: '', score: '', reviewer: '' })
    }
    await loadTabData()
  }

  const deleteTimesheet = async (id: number) => {
    if (!employeeId) return
    await employeesApi.timesheets.delete(employeeId, id)
    if (editingTimesheetId === id) {
      setEditingTimesheetId(null)
      setTimesheetForm({ workDate: '', workedHours: '', overtimeHours: '' })
    }
    await loadTabData()
  }

  return (
    <div className="p-[25px] space-y-4">
      <PageHeader
        title={employeeId ? `Çalışan #${employeeId}` : 'Yeni Çalışan'}
        subtitle={employeeId ? 'Çalışan bilgilerini düzenleyin' : 'Yeni çalışan kaydı oluşturun'}
        actions={
          <>
            <Button variant="outline" onClick={() => navigate('/hr/employees')} disabled={saving}>Listeye Dön</Button>
            <Button onClick={onSave} loading={saving} disabled={saving}>Kaydet</Button>
          </>
        }
      />

      {error && <Alert type="default" variant="danger">{error}</Alert>}

      <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white rounded-xl p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <label className="cursor-pointer">
            {form.profileImage ? <img src={form.profileImage} alt="profile" className="w-20 h-20 rounded-lg object-cover" /> : <div className="w-20 h-20 rounded-lg bg-white/20 flex items-center justify-center">{(form.name[0] || 'N') + (form.surname[0] || 'E')}</div>}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => {
              const f = e.target.files?.[0]
              if (!f) return
              const r = new FileReader()
              r.onload = (ev) => setField('profileImage', (ev.target?.result as string) || '')
              r.readAsDataURL(f)
            }} />
          </label>
          <div>
            <div className="font-semibold">{form.name || 'Yeni'} {form.surname || 'Çalışan'}</div>
            <div className="text-sm text-slate-200">{form.employeeNumber || 'Sicil no bekleniyor'}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={form.isActive ? 'success' : 'warning'}>{form.isActive ? 'Aktif' : 'Pasif'}</Badge>
          <Switch checked={form.isActive} onCheckedChange={(v: boolean) => setField('isActive', v)} label="Durum" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-6 gap-0">
        <div className="lg:col-span-1 px-2 pt-6 space-y-4">
          <Card className="p-3 sticky top-6">
            <h3 className="text-sm font-bold text-gray-900 mb-3">Organizasyon</h3>
            <div className="text-sm space-y-2">
              <div className="flex justify-between gap-2"><span className="text-gray-500">Firma</span><span className="text-right font-medium">{tenants.find((t) => String(t.tenantId) === form.firmId)?.name || '-'}</span></div>
              <div className="flex justify-between gap-2"><span className="text-gray-500">İşyeri</span><span className="text-right font-medium">{organizations.find((o) => String(o.organizationId) === form.locationId)?.name || '-'}</span></div>
              <div className="flex justify-between gap-2"><span className="text-gray-500">Departman</span><span className="text-right font-medium">{departments.find((d) => String(d.departmentId) === form.departmentId)?.name || '-'}</span></div>
              <div className="flex justify-between gap-2"><span className="text-gray-500">Birim</span><span className="text-right font-medium">{departments.find((d) => String(d.departmentId) === form.unitId)?.name || '-'}</span></div>
              <div className="flex justify-between gap-2"><span className="text-gray-500">Alt Birim</span><span className="text-right font-medium">{departments.find((d) => String(d.departmentId) === form.subUnitId)?.name || '-'}</span></div>
            </div>
          </Card>
          <Card className="p-3">
            <h3 className="text-sm font-bold text-gray-900 mb-3">Yönetici</h3>
            <p className="text-sm text-gray-700">Atanmadi</p>
          </Card>
          <Card className="p-3">
            <h3 className="text-sm font-bold text-gray-900 mb-3">Ekibim</h3>
            <p className="text-sm text-gray-700">Uye yok</p>
          </Card>
        </div>

        <div className="lg:col-span-5 px-4">
          <div className="bg-gray-100 rounded-t-lg p-1 flex flex-wrap gap-1">
            {tabs.map((t) => (
              <Button key={t.id} onClick={() => setActiveTab(t.id)} variant={activeTab === t.id ? 'default' : 'ghost'} className={activeTab === t.id ? 'bg-slate-700 text-white' : ''}>
                {t.label}
              </Button>
            ))}
          </div>

          <Card className="p-5 space-y-4 rounded-t-none border-t-0">
            {!employeeId && ['timeoff', 'documents', 'performance', 'timesheet'].includes(activeTab) && (
              <Alert type="default" variant="warning">Bu sekmeler için önce çalışanı kaydedin.</Alert>
            )}

            {/* Pearl birebir: Kişisel Bilgiler - sıra ve etiketler */}
            {activeTab === 'personal' && (
              <>
                <h4 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">Personel Bilgileri</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Input label="Sicil Numarasi" value={form.employeeNumber} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('employeeNumber', e.target.value)} placeholder="Sicil Numarasi" />
                  <Input label="Adı" value={form.name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('name', e.target.value)} placeholder="Adı" />
                  <Input label="İkinci Adı" value={form.secondName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('secondName', e.target.value)} placeholder="İkinci Adı" />
                  <Input label="Soyadı" value={form.surname} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('surname', e.target.value)} placeholder="Soyadı" />
                  <Input label="İşe Giriş Tarihi" type="date" value={form.hireDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('hireDate', e.target.value)} placeholder="mm/dd/yyyy" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Pozisyonu</label>
                    <Select value={form.positionId || '__none__'} onValueChange={(v: string) => setField('positionId', v === '__none__' ? '' : v)} disabled={lookupsLoading}>
                      <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">- Seçin -</SelectItem>
                        {employeeLookups.positions.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Mesleği</label>
                    <Select value={form.jobId || '__none__'} onValueChange={(v: string) => setField('jobId', v === '__none__' ? '' : v)} disabled={lookupsLoading}>
                      <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">- Seçin -</SelectItem>
                        {employeeLookups.jobs.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Unvanı</label>
                    <Select value={form.titleId || '__none__'} onValueChange={(v: string) => setField('titleId', v === '__none__' ? '' : v)} disabled={lookupsLoading}>
                      <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">- Seçin -</SelectItem>
                        {employeeLookups.titles.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Cinsiyeti</label>
                    <Select value={form.sex || 'M'} onValueChange={(v: string) => setField('sex', (v === 'F' ? 'F' : 'M'))}>
                      <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                      <SelectContent>
                        {employeeLookups.genders.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Çalışan Tipi</label>
                    <Select value={form.employeeType || '__none__'} onValueChange={(v: string) => setField('employeeType', v === '__none__' ? '' : v)}>
                      <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Seçiniz</SelectItem>
                        {employeeLookups.employeeTypes.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Ehliyet Sınıfı</label>
                    <Select value={form.licenseClass || '__none__'} onValueChange={(v: string) => setField('licenseClass', v === '__none__' ? '' : v)}>
                      <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Seçiniz</SelectItem>
                        {employeeLookups.driverLicenses.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Input label="Ehliyet Tarihi" type="date" value={form.licenseDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('licenseDate', e.target.value)} placeholder="mm/dd/yyyy" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Öğrenim Durumu</label>
                    <Select value={form.education || '__none__'} onValueChange={(v: string) => setField('education', v === '__none__' ? '' : v)}>
                      <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Seçiniz</SelectItem>
                        {employeeLookups.educationLevels.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Askerlik Durumu</label>
                    <Select value={form.militaryStatus || '__none__'} onValueChange={(v: string) => setField('militaryStatus', v === '__none__' ? '' : v)}>
                      <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Seçiniz</SelectItem>
                        {employeeLookups.militaryStatuses.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Medeni Durumu</label>
                    <Select value={form.maritalStatus || '__none__'} onValueChange={(v: string) => setField('maritalStatus', v === '__none__' ? '' : v)}>
                      <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Seçiniz</SelectItem>
                        {employeeLookups.maritalStatuses.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mt-8">
                  <h4 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">Kimlik Bilgileri</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Input label="T.C. Kimlik Numarası" value={form.idTcNo} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('idTcNo', e.target.value)} placeholder="T.C. Kimlik No" />
                    <Input label="Doğum Tarihi" type="date" value={form.birthDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('birthDate', e.target.value)} />
                    <Input label="Doğum Yeri" value={form.birthPlace} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('birthPlace', e.target.value)} placeholder="Doğum Yeri" />
                    <Input label="Kart Seri No" value={form.serialNo} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('serialNo', e.target.value)} placeholder="Seri No" />
                    <Input label="Geçerlilik Tarihi" type="date" value={form.validityDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('validityDate', e.target.value)} />
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Uyruğu</label>
                      <Select value={form.nationality || '__none__'} onValueChange={(v: string) => setField('nationality', v === '__none__' ? '' : v)} disabled={lookupsLoading}>
                        <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Seçiniz</SelectItem>
                          {countries.map((c) => (
                            <SelectItem key={c.iso2} value={c.name}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input label="Anne Adı" value={form.motherName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('motherName', e.target.value)} placeholder="Anne Adı" />
                    <Input label="Baba Adı" value={form.fatherName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('fatherName', e.target.value)} placeholder="Baba Adı" />
                  </div>
                </div>

                <div className="mt-8">
                  <h4 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">Temel İletişim Bilgileri</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Input
                      label="Cep Telefonu Numarası"
                      value={form.phone}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('phone', e.target.value)}
                      placeholder="Cep Telefonu"
                    />
                    <Input
                      label="Şirket E-posta Adresi"
                      type="email"
                      value={form.email}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('email', e.target.value)}
                      placeholder="E-posta"
                    />
                  </div>
                </div>

                <div className="mt-8">
                  <h4 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">İkamet Adresi</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Input
                      label="Mahalle (Mah.)"
                      value={form.neighborhood}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('neighborhood', e.target.value)}
                      placeholder="Mahalle"
                    />
                    <Input
                      label="Cadde / Sokak Adı"
                      value={form.street}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('street', e.target.value)}
                      placeholder="Cadde / Sokak"
                    />
                    <Input
                      label="Bina Numarası"
                      value={form.buildingNo}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('buildingNo', e.target.value)}
                      placeholder="Bina No"
                    />
                    <Input
                      label="Daire Numarası"
                      value={form.apartmentNo}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('apartmentNo', e.target.value)}
                      placeholder="Daire No"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Ülke</label>
                      <Select value={form.country || '__none__'} onValueChange={(v: string) => {
                        const nextCountry = v === '__none__' ? '' : v
                        setForm((s) => ({
                          ...s,
                          country: nextCountry,
                          province: nextCountry === s.country ? s.province : '',
                          district: nextCountry === s.country ? s.district : '',
                        }))
                      }} disabled={lookupsLoading}>
                        <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Seçiniz</SelectItem>
                          {countries.map((c) => (
                            <SelectItem key={`country-${c.iso2}`} value={c.name}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">İl</label>
                      <Select value={form.province || '__none__'} onValueChange={(v: string) => {
                        const nextProvince = v === '__none__' ? '' : v
                        setForm((s) => ({
                          ...s,
                          province: nextProvince,
                          district: nextProvince === s.province ? s.district : '',
                        }))
                      }} disabled={lookupsLoading || !selectedCountry}>
                        <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Seçiniz</SelectItem>
                          {cities.map((c) => (
                            <SelectItem key={`city-${c.id}`} value={c.name}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {towns.length > 0 ? (
                      <div>
                        <label className="block text-sm font-medium mb-1.5">İlçe</label>
                        <Select value={form.district || '__none__'} onValueChange={(v: string) => setField('district', v === '__none__' ? '' : v)} disabled={lookupsLoading || !selectedCity}>
                          <SelectTrigger><SelectValue placeholder={selectedCity ? 'Seçiniz' : 'Önce il seçiniz'} /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">Seçiniz</SelectItem>
                            {towns.map((t) => (
                              <SelectItem key={`town-${t.id}`} value={t.name}>{t.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ) : (
                      <Input
                        label="İlçe"
                        value={form.district}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('district', e.target.value)}
                        placeholder={selectedCity ? 'İlçe' : 'Önce il seçiniz'}
                        disabled={!selectedCity}
                      />
                    )}
                    <Input
                      label="Posta Kodu"
                      value={form.postalCode}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('postalCode', e.target.value)}
                      placeholder="Posta Kodu"
                    />
                  </div>
                </div>

                <div className="mt-8">
                  <h4 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">Acil Durum Bilgileri</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <Input
                      label="Acil Durum Kişi Adı"
                      value={form.emergencyContactName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('emergencyContactName', e.target.value)}
                      placeholder="Kişi Adı"
                    />
                    <Input
                      label="Acil Durum Telefon Numarası"
                      value={form.emergencyContactPhone}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('emergencyContactPhone', e.target.value)}
                      placeholder="Telefon Numarası"
                    />
                    <div className="w-full">
                      <label className="mb-[10px] md:mb-[12px] text-black dark:text-white font-medium block">Yakınlık Derecesi</label>
                      <Select value={form.emergencyContactRelation || '__none__'} onValueChange={(v: string) => setField('emergencyContactRelation', v === '__none__' ? '' : v)}>
                        <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">- Seçin -</SelectItem>
                          {employeeLookups.emergencyRelations.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
{employeeId && (
                  <div className="mt-8 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-bold text-gray-900 pb-2 border-b border-gray-200 flex-1">Öğrenim Bilgileri</h4>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setEditingEducationId(null)
                          setEducationForm({
                            educationLevel: '',
                            schoolName: '',
                            sectionName: '',
                            graduationStatus: '',
                            graduationYear: '',
                            startYear: '',
                            educationType: '',
                            diplomaGrade: '',
                            countryCity: '',
                            diplomaNumber: '',
                          })
                          setShowEducationModal(true)
                        }}
                      >
                        + Yeni
                      </Button>
                    </div>
                    <div className="overflow-x-auto border border-gray-200 rounded-lg">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-3 py-2 text-left">Eğitim Seviyesi</th>
                            <th className="px-3 py-2 text-left">Okul Adı</th>
                            <th className="px-3 py-2 text-left">Bölüm</th>
                            <th className="px-3 py-2 text-left">Mezuniyet Durumu</th>
                            <th className="px-3 py-2 text-left">Mezuniyet Yılı</th>
                            <th className="px-3 py-2 text-center">İşlemler</th>
                          </tr>
                        </thead>
                        <tbody>
                          {educations.map((x) => (
                            <tr key={x.id} className="border-b border-gray-100">
                              <td className="px-3 py-2">{x.educationLevel || '-'}</td>
                              <td className="px-3 py-2">{x.schoolName || '-'}</td>
                              <td className="px-3 py-2">{x.sectionName || '-'}</td>
                              <td className="px-3 py-2">{x.graduationStatus || '-'}</td>
                              <td className="px-3 py-2">{x.graduationYear || '-'}</td>
                              <td className="px-3 py-2">
                                <div className="flex justify-center gap-2">
                                  <Button
                                    variant="outline"
                                    onClick={() => {
                                      setEditingEducationId(x.id)
                                      setEducationForm({
                                        educationLevel: x.educationLevel ?? '',
                                        schoolName: x.schoolName ?? '',
                                        sectionName: x.sectionName ?? '',
                                        graduationStatus: x.graduationStatus ?? '',
                                        graduationYear: x.graduationYear ? String(x.graduationYear) : '',
                                        startYear: x.startYear ? String(x.startYear) : '',
                                        educationType: x.educationType ?? '',
                                        diplomaGrade: x.diplomaGrade ?? '',
                                        countryCity: x.countryCity ?? '',
                                        diplomaNumber: x.diplomaNumber ?? '',
                                      })
                                      setShowEducationModal(true)
                                    }}
                                  >
                                    Düzenle
                                  </Button>
                                  <Button variant="outline" onClick={() => deleteEducation(x.id)}>Sil</Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                          {educations.length === 0 && (
                            <tr>
                              <td className="px-3 py-3 text-muted-foreground" colSpan={6}>Kayıt yok</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <Dialog open={showEducationModal} onOpenChange={setShowEducationModal}>
                      <DialogContent size="2xl" className="max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
                        <DialogHeader>
                          <DialogTitle>{editingEducationId ? 'Öğrenim Bilgisini Düzenle' : 'Yeni Öğrenim Bilgisi Ekle'}</DialogTitle>
                        </DialogHeader>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-1.5">Eğitim Seviyesi *</label>
                            <Select
                              value={educationForm.educationLevel || '__none__'}
                              onValueChange={(v: string) => setEducationForm((s) => ({ ...s, educationLevel: v === '__none__' ? '' : v }))}
                            >
                              <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">- Seçin -</SelectItem>
                                {employeeLookups.educationLevels.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <Input label="Okul Adı *" value={educationForm.schoolName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEducationForm((s) => ({ ...s, schoolName: e.target.value }))} placeholder="Üniversite / Lise adı" />
                          <Input label="Bölüm *" value={educationForm.sectionName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEducationForm((s) => ({ ...s, sectionName: e.target.value }))} placeholder="Program adı" />
                          <div>
                            <label className="block text-sm font-medium mb-1.5">Mezuniyet Durumu *</label>
                            <Select
                              value={educationForm.graduationStatus || '__none__'}
                              onValueChange={(v: string) => setEducationForm((s) => ({ ...s, graduationStatus: v === '__none__' ? '' : v }))}
                            >
                              <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">- Seçin -</SelectItem>
                                {employeeLookups.graduationStatuses.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <Input label="Mezuniyet Yılı *" type="number" value={educationForm.graduationYear} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEducationForm((s) => ({ ...s, graduationYear: e.target.value }))} placeholder="YYYY" />
                          <Input label="Başlangıç Yılı" type="number" value={educationForm.startYear} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEducationForm((s) => ({ ...s, startYear: e.target.value }))} placeholder="YYYY" />
                          <div>
                            <label className="block text-sm font-medium mb-1.5">Eğitim Türü</label>
                            <Select
                              value={educationForm.educationType || '__none__'}
                              onValueChange={(v: string) => setEducationForm((s) => ({ ...s, educationType: v === '__none__' ? '' : v }))}
                            >
                              <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">- Seçin -</SelectItem>
                                {employeeLookups.educationTypes.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <Input label="Diploma Notu" value={educationForm.diplomaGrade} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEducationForm((s) => ({ ...s, diplomaGrade: e.target.value }))} />
                          <Input label="Ülke / Şehir" value={educationForm.countryCity} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEducationForm((s) => ({ ...s, countryCity: e.target.value }))} placeholder="Özellikle yurtdışı için" />
                          <Input label="Diploma Numarası" value={educationForm.diplomaNumber} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEducationForm((s) => ({ ...s, diplomaNumber: e.target.value }))} placeholder="Resmî işlemler için" />
                        </div>

                        <DialogFooter>
                          <Button variant="outline" onClick={() => setShowEducationModal(false)}>İptal</Button>
                          <Button onClick={addEducation}>{editingEducationId ? 'Güncelle' : 'Kaydet'}</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                )}
              </>
            )}

            {/* Pearl birebir: Organizasyon */}
            {activeTab === 'job' && (
              <div className="space-y-6">
                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">Organizasyon Bilgileri</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Firma</label>
                      <Select
                        value={form.firmId || '__none__'}
                        onValueChange={(v: string) => {
                          const newFirmId = v === '__none__' ? '' : v
                          setForm((s) => ({ ...s, firmId: newFirmId, organizationId: '', locationId: '', departmentId: '', unitId: '', subUnitId: '' }))
                          if (newFirmId) void loadOrganizationsByFirm(Number(newFirmId))
                        }}
                        disabled={lookupsLoading}
                      >
                        <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">- Seçin -</SelectItem>
                          {tenants.map((t) => <SelectItem key={t.tenantId} value={String(t.tenantId)}>{t.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">İşyeri</label>
                      <Select
                        value={form.organizationId || '__none__'}
                        onValueChange={(v: string) => setForm((s) => ({ ...s, organizationId: v === '__none__' ? '' : v, departmentId: '', unitId: '', subUnitId: '' }))}
                        disabled={lookupsLoading || !form.firmId}
                      >
                        <SelectTrigger><SelectValue placeholder={form.firmId ? 'Seçiniz' : 'Önce Firma Seçiniz'} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">- Seçin -</SelectItem>
                          {organizations.map((o) => <SelectItem key={o.organizationId} value={String(o.organizationId)}>{`${o.code ?? ''} - ${o.name}`.trim()}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Departman</label>
                      <Select
                        value={form.departmentId || '__none__'}
                        onValueChange={(v: string) => setForm((s) => ({ ...s, departmentId: v === '__none__' ? '' : v, unitId: '', subUnitId: '' }))}
                        disabled={!form.organizationId}
                      >
                        <SelectTrigger><SelectValue placeholder={form.organizationId ? 'Seçiniz' : 'Önce İşyeri Seçiniz'} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">- Seçin -</SelectItem>
                          {departments.filter((d) => !d.parentId && (!form.organizationId || d.organizationId === Number(form.organizationId))).map((d) => (
                            <SelectItem key={d.departmentId} value={String(d.departmentId)}>{`${d.code ?? ''} - ${d.name}`.trim()}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Birim</label>
                      <Select value={form.unitId || '__none__'} onValueChange={(v: string) => setForm((s) => ({ ...s, unitId: v === '__none__' ? '' : v, subUnitId: '' }))}>
                        <SelectTrigger><SelectValue placeholder={form.departmentId ? 'Seçiniz' : 'Önce Departman Seçiniz'} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">- Seçin -</SelectItem>
                          {unitOptions.map((d) => <SelectItem key={d.departmentId} value={String(d.departmentId)}>{`${d.code ?? ''} - ${d.name}`.trim()}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Alt Birim</label>
                      <Select value={form.subUnitId || '__none__'} onValueChange={(v: string) => setField('subUnitId', v === '__none__' ? '' : v)}>
                        <SelectTrigger><SelectValue placeholder={form.unitId ? 'Seçiniz' : 'Önce Birim Seçiniz'} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">- Seçin -</SelectItem>
                          {subUnitOptions.map((d) => <SelectItem key={d.departmentId} value={String(d.departmentId)}>{`${d.code ?? ''} - ${d.name}`.trim()}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

              </div>
            )}

            {/* Pearl birebir: Çalışma Bilgileri */}
            {activeTab === 'employment' && (
              <div className="space-y-6">
                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">Ödeme Bilgileri</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Input label="Ücreti" type="number" value={form.salary} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('salary', e.target.value)} placeholder="Ücret" />
                    <Input label="Ücret Döviz Türü" value={form.salaryCurrency} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('salaryCurrency', e.target.value)} placeholder="Döviz Türü" />
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Ödeme Tipi</label>
                      <Select value={form.paymentFrequency || '__none__'} onValueChange={(v: string) => setField('paymentFrequency', v === '__none__' ? '' : v)}>
                        <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">- Seçin -</SelectItem>
                          {employeeLookups.paymentFrequencies.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Hesaplama Şekli</label>
                      <Select value={form.calculationType || '__none__'} onValueChange={(v: string) => setField('calculationType', v === '__none__' ? '' : v)}>
                        <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">- Seçin -</SelectItem>
                          {employeeLookups.calculationTypes.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Ödeme Şekli</label>
                      <Select value={form.paymentMethod || '__none__'} onValueChange={(v: string) => setField('paymentMethod', v === '__none__' ? '' : v)}>
                        <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">- Seçin -</SelectItem>
                          {employeeLookups.paymentMethods.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Ücret Çalışma Tipi</label>
                      <Select value={form.salaryWorkType || '__none__'} onValueChange={(v: string) => setField('salaryWorkType', v === '__none__' ? '' : v)}>
                        <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">- Seçin -</SelectItem>
                          {employeeLookups.salaryWorkTypes.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Switch checked={form.minimumWage} onCheckedChange={(v: boolean) => setField('minimumWage', v)} label="Asgari Ücretli" />
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">Çalışma Bilgileri</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Çalışma Takvimi</label>
                      <Select value={form.workCalendar || '__none__'} onValueChange={(v: string) => setField('workCalendar', v === '__none__' ? '' : v)}>
                        <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">- Seçin -</SelectItem>
                          {employeeLookups.workCalendars.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Görev Tipi</label>
                      <Select value={form.dutyType || '__none__'} onValueChange={(v: string) => setField('dutyType', v === '__none__' ? '' : v)}>
                        <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">- Seçin -</SelectItem>
                          {employeeLookups.dutyTypes.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-1.5">Çalışma Şekli</label>
                      <Select value={form.workMode || '__none__'} onValueChange={(v: string) => setField('workMode', v === '__none__' ? '' : v)}>
                        <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">- Seçin -</SelectItem>
                          {employeeLookups.workModes.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                    <Input
                      label="Aylık Çalışma Günü"
                      type="number"
                      value={form.monthlyWorkDays}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('monthlyWorkDays', e.target.value)}
                      placeholder="Gün sayısını girin"
                    />
                    <Input
                      label="Haftalık Çalışma Saati"
                      type="number"
                      step="0.5"
                      value={form.weeklyWorkHours}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('weeklyWorkHours', e.target.value)}
                      placeholder="Saat sayısını girin"
                    />
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">Sözleşme Bilgileri</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Sözleşme Şekli</label>
                      <Select value={form.contractType || '__none__'} onValueChange={(v: string) => setField('contractType', v === '__none__' ? '' : v)}>
                        <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">- Seçin -</SelectItem>
                          {employeeLookups.contractTypes.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input label="Sözleşme Başlangıç Tarihi" type="date" value={form.contractBegDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('contractBegDate', e.target.value)} />
                    <Input label="Sözleşme Bitiş Tarihi" type="date" value={form.contractEndDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('contractEndDate', e.target.value)} />
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">Deneme Süresi Bilgileri</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Input
                      label="Deneme Süresi (ay)"
                      type="number"
                      value={form.trialPeriodMonths}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('trialPeriodMonths', e.target.value)}
                      placeholder="Ay sayısını girin"
                    />
                    <Input
                      label="Deneme Süresi Sonu Ücreti"
                      type="number"
                      value={form.trialPeriodEndSalary}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('trialPeriodEndSalary', e.target.value)}
                      placeholder="Ücret girin"
                    />
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">SGK Bilgileri</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Input label="Sosyal Güvenlik Statüsü" type="number" value={form.sscStatus} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('sscStatus', e.target.value)} placeholder="Örn: 1" />
                    <Input label="S.G. Başlangıç Tarihi" type="date" value={form.insuranceStartDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('insuranceStartDate', e.target.value)} />
                    <Input label="Sosyal Güvenlik Numarası" value={form.sscNo} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('sscNo', e.target.value)} placeholder="SGK numarası" />
                    <Input label="SGK'da Kayıtlı İsim" value={form.sscRegisteredName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('sscRegisteredName', e.target.value)} placeholder="SGK adı" />
                    <Input label="SGK'da Kayıtlı Soyisim" value={form.sscRegisteredSurname} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('sscRegisteredSurname', e.target.value)} placeholder="SGK soyadı" />
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">Banka Bilgileri</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Banka Şubesi</label>
                      <Select
                        value={form.bankBranch || '__none__'}
                        onValueChange={(v: string) => {
                          if (v === '__none__') {
                            setForm((s) => ({ ...s, bankBranch: '', branchName: '', bankCode: '', bankName: '' }))
                            return
                          }
                          setForm((s) => ({
                            ...s,
                            bankBranch: v,
                          }))
                        }}
                      >
                        <SelectTrigger><SelectValue placeholder="Şube Seç" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Şube Seç</SelectItem>
                          {employeeLookups.bankBranches.map((x) => (
                            <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input label="Şube Adı" value={form.branchName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('branchName', e.target.value)} placeholder="Şube adı" />
                    <Input label="Banka Kodu" value={form.bankCode} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('bankCode', e.target.value)} placeholder="Banka kodu" />
                    <Input label="Banka Adı" value={form.bankName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('bankName', e.target.value)} placeholder="Banka adı" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                    <Input label="Hesap Numarası" value={form.accountNo} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('accountNo', e.target.value)} placeholder="Hesap numarasını girin" />
                    <div className="lg:col-span-3">
                      <Input label="IBAN" value={form.iban} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('iban', e.target.value)} placeholder="IBAN'ı girin" />
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">Muafiyetler</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 border border-gray-200 rounded-md bg-white">
                      <Switch
                        checked={form.incomeTaxExemption}
                        onCheckedChange={(v: boolean) => setField('incomeTaxExemption', v)}
                        label="Gelir Vergisi Muafiyeti"
                      />
                    </div>
                    <div className="p-4 border border-gray-200 rounded-md bg-white">
                      <Switch
                        checked={form.stampTaxExemption}
                        onCheckedChange={(v: boolean) => setField('stampTaxExemption', v)}
                        label="Damga Vergisi Muafiyeti"
                      />
                    </div>
                    <div className="p-4 border border-gray-200 rounded-md bg-white">
                      <Switch
                        checked={form.minimumWageIncomeTaxExemption}
                        onCheckedChange={(v: boolean) => setField('minimumWageIncomeTaxExemption', v)}
                        label="Asgari Ücret Gelir Vergisi Muafiyeti"
                      />
                    </div>
                    <div className="p-4 border border-gray-200 rounded-md bg-white">
                      <Switch
                        checked={form.minimumWageStampTaxExemption}
                        onCheckedChange={(v: boolean) => setField('minimumWageStampTaxExemption', v)}
                        label="Asgari Ücret Damga Vergisi Muafiyeti"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">Kanun ve Yasalar</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1.5">İndirim Kanunu</label>
                      <Select value={form.discountLaw || '__none__'} onValueChange={(v: string) => setField('discountLaw', v === '__none__' ? '' : v)}>
                        <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">- Seçin -</SelectItem>
                          {employeeLookups.discountLaws.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Çalışma Kanunu</label>
                      <Select value={form.laborLaw || '__none__'} onValueChange={(v: string) => setField('laborLaw', v === '__none__' ? '' : v)}>
                        <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">- Seçin -</SelectItem>
                          {employeeLookups.laborLaws.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Belge Türü</label>
                      <Select value={form.employeeDocumentType || '__none__'} onValueChange={(v: string) => setField('employeeDocumentType', v === '__none__' ? '' : v)}>
                        <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">- Seçin -</SelectItem>
                          {employeeLookups.employeeDocumentTypes.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">AR-GE Yasası</label>
                      <Select value={form.rndLaw || '__none__'} onValueChange={(v: string) => setField('rndLaw', v === '__none__' ? '' : v)}>
                        <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">- Seçin -</SelectItem>
                          {employeeLookups.rndLaws.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">İstihdam Yasası</label>
                      <Select value={form.employmentLaw || '__none__'} onValueChange={(v: string) => setField('employmentLaw', v === '__none__' ? '' : v)}>
                        <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">- Seçin -</SelectItem>
                          {employeeLookups.employmentLaws.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input
                      label="İstihdam Teşvik Bitişi"
                      type="date"
                      value={form.employmentIncentiveEndDate}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('employmentIncentiveEndDate', e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">Engellilik Bilgileri</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Engellilik Derecesi</label>
                      <Select value={form.disabilityDegree || '__none__'} onValueChange={(v: string) => setField('disabilityDegree', v === '__none__' ? '' : v)}>
                        <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">- Seçin -</SelectItem>
                          {employeeLookups.disabilityDegrees.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input
                      label="Engellilik Oranı (Yüzde)"
                      type="number"
                      min="0"
                      max="100"
                      value={form.disabilityRate}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('disabilityRate', e.target.value)}
                      placeholder="0-100 arasında değer girin"
                    />
                    <div className="lg:col-span-2">
                      <Input
                        label="Engelli Grubu"
                        value={form.disabilityGroup}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('disabilityGroup', e.target.value)}
                        placeholder="Engelli grubu adını girin"
                      />
                    </div>
                  </div>
                </div>

              </div>
            )}

            {activeTab === 'timeoff' && employeeId && (
              <div className="space-y-5">

                {/* İzin Bakiyesi — Özet Kartlar */}
                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">İzin Bakiyesi</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="rounded-xl border border-gray-200 p-4 bg-white space-y-2">
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Toplam İzin Talebi</div>
                      <div className="text-3xl font-bold text-gray-900">{timeOffStats.total}</div>
                      <div className="text-xs text-gray-400">kayıt</div>
                    </div>
                    <div className="rounded-xl border border-green-200 p-4 bg-green-50 space-y-2">
                      <div className="text-xs font-medium text-green-700 uppercase tracking-wide">Onaylanan</div>
                      <div className="text-3xl font-bold text-green-800">{timeOffStats.approved}</div>
                      <div className="w-full bg-green-200 rounded-full h-1.5 mt-1">
                        <div
                          className="bg-green-600 h-1.5 rounded-full"
                          style={{ width: timeOffStats.total > 0 ? `${Math.round((timeOffStats.approved / timeOffStats.total) * 100)}%` : '0%' }}
                        />
                      </div>
                    </div>
                    <div className="rounded-xl border border-amber-200 p-4 bg-amber-50 space-y-2">
                      <div className="text-xs font-medium text-amber-700 uppercase tracking-wide">Beklemede</div>
                      <div className="text-3xl font-bold text-amber-800">{timeOffStats.pending}</div>
                      <div className="w-full bg-amber-200 rounded-full h-1.5 mt-1">
                        <div
                          className="bg-amber-500 h-1.5 rounded-full"
                          style={{ width: timeOffStats.total > 0 ? `${Math.round((timeOffStats.pending / timeOffStats.total) * 100)}%` : '0%' }}
                        />
                      </div>
                    </div>
                    <div className="rounded-xl border border-blue-200 p-4 bg-blue-50 space-y-2">
                      <div className="text-xs font-medium text-blue-700 uppercase tracking-wide">Toplam Kullanılan Gün</div>
                      <div className="text-3xl font-bold text-blue-800">{timeOffStats.totalDays}</div>
                      <div className="text-xs text-blue-500">iş günü</div>
                    </div>
                  </div>
                </div>

                {/* İzin Talebi Formu */}
                <div className="rounded-xl border border-gray-200 bg-white">
                  <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                    <h4 className="text-sm font-semibold text-gray-900">
                      {editingTimeOffId ? 'İzin Talebi Güncelle' : 'Yeni İzin Talebi'}
                    </h4>
                    {editingTimeOffId && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          setEditingTimeOffId(null)
                          setTimeOffForm({ timeOffType: '', startDate: '', endDate: '', approvalStatus: '', notes: '' })
                        }}
                      >
                        Yeni Kayıt
                      </Button>
                    )}
                  </div>

                  <div className="p-5 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1.5">İzin Türü</label>
                        <Select
                          value={timeOffForm.timeOffType || '__none__'}
                          onValueChange={(v: string) => setTimeOffForm((s) => ({ ...s, timeOffType: v === '__none__' ? '' : v }))}
                          disabled={lookupsLoading}
                        >
                          <SelectTrigger><SelectValue placeholder="İzin türü seçin" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">- Seçin -</SelectItem>
                            {employeeLookups.timeOffTypes.map((x) => (
                              <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <Input
                        label="Başlangıç Tarihi"
                        type="date"
                        value={timeOffForm.startDate}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTimeOffForm((s) => ({ ...s, startDate: e.target.value }))}
                      />

                      <Input
                        label="Bitiş Tarihi"
                        type="date"
                        value={timeOffForm.endDate}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTimeOffForm((s) => ({ ...s, endDate: e.target.value }))}
                      />

                      <div>
                        <label className="block text-sm font-medium mb-1.5">Onay Durumu</label>
                        <Select
                          value={timeOffForm.approvalStatus || '__none__'}
                          onValueChange={(v: string) => setTimeOffForm((s) => ({ ...s, approvalStatus: v === '__none__' ? '' : v }))}
                          disabled={lookupsLoading}
                        >
                          <SelectTrigger><SelectValue placeholder="Durum seçin" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">- Seçin -</SelectItem>
                            {employeeLookups.approvalStatuses.map((x) => (
                              <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1.5">Süre (Gün)</label>
                        <Input
                          value={
                            timeOffForm.startDate && timeOffForm.endDate
                              ? String(calculateDurationDays(timeOffForm.startDate, timeOffForm.endDate) ?? '-')
                              : '-'
                          }
                          disabled
                          placeholder="Otomatik hesaplanır"
                        />
                      </div>
                    </div>

                    <Textarea
                      label="Notlar"
                      value={timeOffForm.notes}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setTimeOffForm((s) => ({ ...s, notes: e.target.value }))}
                      placeholder="İzin ile ilgili açıklama veya not girin"
                    />

                    <div className="flex items-center gap-3 pt-1">
                      <Button onClick={addTimeOff} disabled={!timeOffForm.startDate || !timeOffForm.endDate}>
                        {editingTimeOffId ? 'İzin Talebini Güncelle' : 'İzin Talebi Ekle'}
                      </Button>
                      {(timeOffForm.timeOffType || timeOffForm.startDate || timeOffForm.endDate || timeOffForm.notes) && (
                        <Button
                          variant="ghost"
                          onClick={() => {
                            setEditingTimeOffId(null)
                            setTimeOffForm({ timeOffType: '', startDate: '', endDate: '', approvalStatus: '', notes: '' })
                          }}
                        >
                          Temizle
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* İzin Geçmişi */}
                <div className="rounded-xl border border-gray-200 bg-white">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-4 border-b border-gray-100">
                    <h4 className="text-sm font-semibold text-gray-900 flex-1">İzin Geçmişi</h4>
                    <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                      <div className="w-full sm:w-56">
                        <Input
                          value={timeOffQuery}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTimeOffQuery(e.target.value)}
                          placeholder="İzin türü veya not ile ara..."
                        />
                      </div>
                      <div className="w-full sm:w-44">
                        <Select
                          value={timeOffFilter}
                          onValueChange={(v: string) => setTimeOffFilter(v as 'all' | 'approved' | 'pending' | 'rejected')}
                        >
                          <SelectTrigger><SelectValue placeholder="Tümü" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Tümü</SelectItem>
                            <SelectItem value="approved">Onaylı</SelectItem>
                            <SelectItem value="pending">Beklemede</SelectItem>
                            <SelectItem value="rejected">Reddedildi</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left font-medium text-gray-600">İzin Türü</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-600">Başlangıç</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-600">Bitiş</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-600">Süre (Gün)</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-600">Onay Durumu</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-600">Notlar</th>
                          <th className="px-4 py-3 text-center font-medium text-gray-600">İşlemler</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTimeOffs.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400">
                              {timeOffQuery || timeOffFilter !== 'all' ? 'Arama kriterlerine uygun kayıt bulunamadı.' : 'Henüz izin talebi bulunmuyor.'}
                            </td>
                          </tr>
                        ) : (
                          filteredTimeOffs.map((x) => {
                            const status = normalizeApprovalStatus(x.approvalStatus)
                            const statusLabel = status === 'approved' ? 'Onaylı' : status === 'rejected' ? 'Reddedildi' : 'Beklemede'
                            const statusVariant = status === 'approved' ? 'success' : status === 'rejected' ? 'danger' : 'warning'
                            const duration = x.durationDays ?? calculateDurationDays(x.startDate?.slice(0, 10), x.endDate?.slice(0, 10))
                            const isEditing = editingTimeOffId === x.id
                            return (
                              <tr key={x.id} className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${isEditing ? 'bg-blue-50' : ''}`}>
                                <td className="px-4 py-3 font-medium text-gray-900">{x.timeOffType || '-'}</td>
                                <td className="px-4 py-3 text-gray-700">{x.startDate?.slice(0, 10) || '-'}</td>
                                <td className="px-4 py-3 text-gray-700">{x.endDate?.slice(0, 10) || '-'}</td>
                                <td className="px-4 py-3 text-gray-700">{duration != null ? `${duration} gün` : '-'}</td>
                                <td className="px-4 py-3">
                                  <Badge variant={statusVariant as any}>{statusLabel}</Badge>
                                </td>
                                <td className="px-4 py-3 text-gray-500 max-w-xs truncate" title={x.notes || undefined}>{x.notes || '-'}</td>
                                <td className="px-4 py-3">
                                  <div className="flex justify-center gap-2">
                                    <Button
                                      variant="outline"
                                      onClick={() => {
                                        setEditingTimeOffId(x.id)
                                        setTimeOffForm({
                                          timeOffType: x.timeOffType ?? '',
                                          startDate: x.startDate?.slice(0, 10) ?? '',
                                          endDate: x.endDate?.slice(0, 10) ?? '',
                                          approvalStatus: x.approvalStatus ?? '',
                                          notes: x.notes ?? '',
                                        })
                                      }}
                                    >
                                      Düzenle
                                    </Button>
                                    <Button variant="outline" onClick={() => deleteTimeOff(x.id)}>Sil</Button>
                                  </div>
                                </td>
                              </tr>
                            )
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  {filteredTimeOffs.length > 0 && (
                    <div className="px-4 py-3 border-t border-gray-100 text-xs text-gray-400">
                      {filteredTimeOffs.length} kayıt gösteriliyor
                      {(timeOffQuery || timeOffFilter !== 'all') && ` · ${timeOffs.length} toplam kayıttan filtrelendi`}
                    </div>
                  )}
                </div>

              </div>
            )}

            {activeTab === 'documents' && employeeId && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-bold text-gray-900">Belgeler</h4>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditingDocumentId(null)
                      setDocumentForm({ name: '', documentType: '', notes: '' })
                    }}
                  >
                    Temizle
                  </Button>
                </div>

                <div className="rounded-lg border border-gray-200 p-4 bg-white space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input label="Belge Adı *" value={documentForm.name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDocumentForm((s) => ({ ...s, name: e.target.value }))} placeholder="Belge adını girin" />
                    <Input label="Belge Türü" value={documentForm.documentType} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDocumentForm((s) => ({ ...s, documentType: e.target.value }))} placeholder="Belge türünü girin" />
                  </div>
                  <Textarea label="Not" value={documentForm.notes} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDocumentForm((s) => ({ ...s, notes: e.target.value }))} />
                  <Button onClick={addDocument}>{editingDocumentId ? 'Belge Güncelle' : 'Belge Ekle'}</Button>
                </div>

                <div className="overflow-x-auto border border-gray-200 rounded-lg mt-4">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-3 py-2 text-left">Belge Adı</th>
                        <th className="px-3 py-2 text-left">Tür</th>
                        <th className="px-3 py-2 text-left">Not</th>
                        <th className="px-3 py-2 text-center">İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {documents.map((x) => (
                        <tr key={x.id} className="border-b border-gray-100">
                          <td className="px-3 py-2">{x.name || '-'}</td>
                          <td className="px-3 py-2">{x.documentType || '-'}</td>
                          <td className="px-3 py-2">{x.notes || '-'}</td>
                          <td className="px-3 py-2">
                            <div className="flex justify-center gap-2">
                              <Button variant="outline" onClick={() => { setEditingDocumentId(x.id); setDocumentForm({ name: x.name ?? '', documentType: x.documentType ?? '', notes: x.notes ?? '' }) }}>Düzenle</Button>
                              <Button variant="outline" onClick={() => deleteDocument(x.id)}>Sil</Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {documents.length === 0 && (
                        <tr><td className="px-3 py-3 text-muted-foreground" colSpan={4}>Kayıt yok</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {activeTab === 'performance' && employeeId && (
              <>
                <div className="rounded-lg border border-gray-200 p-4 bg-white space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-gray-900">{editingPerformanceId ? 'Performans Kaydı Güncelle' : 'Yeni Performans Kaydı'}</h4>
                    <Button variant="outline" onClick={() => { setEditingPerformanceId(null); setPerformanceForm({ reviewDate: '', score: '', reviewer: '' }) }}>Temizle</Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Input label="Değerlendirme Tarihi *" type="date" value={performanceForm.reviewDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPerformanceForm((s) => ({ ...s, reviewDate: e.target.value }))} />
                    <Input label="Skor" type="number" value={performanceForm.score} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPerformanceForm((s) => ({ ...s, score: e.target.value }))} placeholder="0-100" />
                    <Input label="Değerlendiren" value={performanceForm.reviewer} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPerformanceForm((s) => ({ ...s, reviewer: e.target.value }))} placeholder="Değerlendiren kişi" />
                  </div>
                  <Button onClick={addPerformance}>{editingPerformanceId ? 'Performans Güncelle' : 'Performans Ekle'}</Button>
                </div>

                <div className="overflow-x-auto border border-gray-200 rounded-lg mt-4">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-3 py-2 text-left">Değerlendirme Tarihi</th>
                        <th className="px-3 py-2 text-left">Skor</th>
                        <th className="px-3 py-2 text-left">Değerlendiren</th>
                        <th className="px-3 py-2 text-center">İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {performances.map((x) => (
                        <tr key={x.id} className="border-b border-gray-100">
                          <td className="px-3 py-2">{x.reviewDate?.slice(0, 10) || '-'}</td>
                          <td className="px-3 py-2">{x.score ?? '-'}</td>
                          <td className="px-3 py-2">{x.reviewer || '-'}</td>
                          <td className="px-3 py-2">
                            <div className="flex justify-center gap-2">
                              <Button variant="outline" onClick={() => { setEditingPerformanceId(x.id); setPerformanceForm({ reviewDate: x.reviewDate?.slice(0, 10) ?? '', score: x.score != null ? String(x.score) : '', reviewer: x.reviewer ?? '' }) }}>Düzenle</Button>
                              <Button variant="outline" onClick={() => deletePerformance(x.id)}>Sil</Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {performances.length === 0 && (
                        <tr><td className="px-3 py-3 text-muted-foreground" colSpan={4}>Kayıt yok</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {activeTab === 'timesheet' && employeeId && (
              <>
                <div className="rounded-lg border border-gray-200 p-4 bg-white space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-gray-900">{editingTimesheetId ? 'Puantaj Kaydı Güncelle' : 'Yeni Puantaj Kaydı'}</h4>
                    <Button variant="outline" onClick={() => { setEditingTimesheetId(null); setTimesheetForm({ workDate: '', workedHours: '', overtimeHours: '' }) }}>Temizle</Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Input label="Çalışma Tarihi *" type="date" value={timesheetForm.workDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTimesheetForm((s) => ({ ...s, workDate: e.target.value }))} />
                    <Input label="Çalışma Saati *" type="number" step="0.5" value={timesheetForm.workedHours} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTimesheetForm((s) => ({ ...s, workedHours: e.target.value }))} placeholder="Saat" />
                    <Input label="Mesai Saati" type="number" step="0.5" value={timesheetForm.overtimeHours} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTimesheetForm((s) => ({ ...s, overtimeHours: e.target.value }))} placeholder="Mesai saati" />
                  </div>
                  <Button onClick={addTimesheet}>{editingTimesheetId ? 'Puantaj Güncelle' : 'Puantaj Ekle'}</Button>
                </div>

                <div className="overflow-x-auto border border-gray-200 rounded-lg mt-4">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-3 py-2 text-left">Çalışma Tarihi</th>
                        <th className="px-3 py-2 text-left">Çalışma Saati</th>
                        <th className="px-3 py-2 text-left">Mesai Saati</th>
                        <th className="px-3 py-2 text-center">İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {timesheets.map((x) => (
                        <tr key={x.id} className="border-b border-gray-100">
                          <td className="px-3 py-2">{x.workDate?.slice(0, 10) || '-'}</td>
                          <td className="px-3 py-2">{x.workedHours != null ? `${x.workedHours} saat` : '-'}</td>
                          <td className="px-3 py-2">{x.overtimeHours != null ? `${x.overtimeHours} saat` : '-'}</td>
                          <td className="px-3 py-2">
                            <div className="flex justify-center gap-2">
                              <Button variant="outline" onClick={() => { setEditingTimesheetId(x.id); setTimesheetForm({ workDate: x.workDate?.slice(0, 10) ?? '', workedHours: x.workedHours != null ? String(x.workedHours) : '', overtimeHours: x.overtimeHours != null ? String(x.overtimeHours) : '' }) }}>Düzenle</Button>
                              <Button variant="outline" onClick={() => deleteTimesheet(x.id)}>Sil</Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {timesheets.length === 0 && (
                        <tr><td className="px-3 py-3 text-muted-foreground" colSpan={4}>Kayıt yok</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  )
}





